import { z } from "zod";

export const FilterByIdListPayloadSchema = z.object({
  type: z.literal("filter_by_id_list"),
  idList: z.array(z.string().nullable()),
  mode: z.union([z.literal("included"), z.literal("not_included")]),
});

export type FilterByIdListPayload = z.infer<typeof FilterByIdListPayloadSchema>;
export type FilterByIdListMode = FilterByIdListPayload["mode"];
export type FilterByIdList = FilterByIdListPayload["idList"];

/**
 * Filters based on the payload and the provided id or id array.
 * @param payload The filtering payload.
 * @param id A single string, null, undefined, or an array of strings.
 * @returns true if the id(s) match the filter criteria, false otherwise.
 */
export const filterByIdList = (
  payload: FilterByIdListPayload,
  idOrIds: string | string[] | null | undefined,
): boolean => {
  const idArray = Array.isArray(idOrIds) ? idOrIds : [idOrIds ?? null]; // Normalize id to an array

  if (payload.mode === "included") {
    if (payload.idList.includes(null) && idArray.length === 0) {
      return true;
    }
    // Check if any id in the array is included in the idList
    return idArray.some((singleId) => payload.idList.includes(singleId));
  }

  if (payload.mode === "not_included") {
    if (payload.idList.includes(null) && idArray.length === 0) {
      return false;
    }
    // Check if all ids in the array are not included in the idList
    return idArray.every((singleId) => !payload.idList.includes(singleId));
  }

  // fall through; shouldn't happen
  return true;
};

export const filterByIdListWithUnknownPayload = (
  payload: unknown,
  idOrIds: string | string[] | null | undefined,
): boolean => {
  const safePayload = FilterByIdListPayloadSchema.safeParse(payload);
  if (!safePayload.success) {
    return true; // if we cannot apply the filter then don't filter at all
  }

  return filterByIdList(safePayload.data, idOrIds);
};

export const encodeFilterByIdList = (columnId: string, filter: FilterByIdListPayload): string => {
  const key = `f_${columnId}`;
  const value = encodeURIComponent(JSON.stringify(filter));
  return `${key}=${value}`;
};

export const decodeFilterByIdList = (
  columnId: string,
  urlParams: URLSearchParams,
  surpressErrorMessage?: boolean,
): FilterByIdListPayload | null => {
  const key = `f_${columnId}`;

  const value = urlParams.get(key);
  if (!value) {
    return null;
  }

  try {
    const filter = JSON.parse(decodeURIComponent(value));
    const result = FilterByIdListPayloadSchema.safeParse(filter);
    if (result.success) {
      return result.data;
    } else {
      if (!surpressErrorMessage) {
        console.error(`Invalid filter for column ${columnId}:`, result.error);
      }
      return null;
    }
  } catch (error) {
    if (!surpressErrorMessage) {
      console.error(`Failed to parse filter for column ${columnId}:`, error);
    }
    return null;
  }
};
